import { useCallback, useEffect, useRef, useState } from "react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { AlertTriangle, Loader2, LogIn, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { getStripe, getStripeEnvironment } from "@/lib/stripe";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  priceId: string;
  quantity?: number;
  customerEmail?: string;
  customerName?: string;
  userId?: string;
  returnUrl?: string;
}

/**
 * Server-error UI contract:
 *  - 401/403  → redirect to /auth (session missing / expired). Handled below.
 *  - 5xx      → premium retryable error card. Parent-held selections
 *               (priceId, email, name) are untouched, so retry does not
 *               lose the user's input.
 *  - Other    → generic retry card with the server-provided message.
 *
 * We pre-fetch the clientSecret ourselves BEFORE mounting Stripe's
 * <EmbeddedCheckoutProvider>. Doing it inside the provider's
 * `fetchClientSecret` callback would surface failures as an internal
 * Stripe error state we can't style — pre-fetching lets us own the
 * error UI end-to-end and control retries deterministically.
 */

type CheckoutState =
  | { phase: "loading" }
  | { phase: "ready"; clientSecret: string }
  | { phase: "needs-auth"; nextPath: string }
  | { phase: "error"; status: number | null; message: string; retryable: boolean };

interface InvokeError {
  message?: string;
  context?: { status?: number };
}

function classifyError(status: number | null, serverMessage?: string): {
  title: string;
  message: string;
  retryable: boolean;
} {
  if (status === 503) {
    return {
      title: "Checkout is temporarily unavailable",
      message:
        "Our payment provider is briefly unreachable. Your selections are saved — please try again in a moment.",
      retryable: true,
    };
  }
  if (status !== null && status >= 500) {
    return {
      title: "We couldn't start your checkout",
      message:
        "Something on our side interrupted the request. Your selections are saved — please try again.",
      retryable: true,
    };
  }
  return {
    title: "We couldn't start your checkout",
    message: serverMessage || "An unexpected error occurred. Please try again.",
    retryable: true,
  };
}

export function StripeEmbeddedCheckout({
  priceId,
  quantity,
  customerEmail,
  customerName,
  userId,
  returnUrl,
}: Props) {
  const [state, setState] = useState<CheckoutState>({ phase: "loading" });
  const [attempt, setAttempt] = useState(0);
  // Guards the async effect against setState-after-unmount and races when
  // the user retries before the previous invoke has resolved.
  const activeAttemptRef = useRef(0);

  const requestClientSecret = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: {
        priceId,
        quantity,
        customerEmail,
        customerName,
        userId,
        returnUrl:
          returnUrl ??
          `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`,
        environment: getStripeEnvironment(),
      },
    });

    const status = (error as InvokeError | null)?.context?.status ?? null;

    // Auth failures: surface a "Sign in required to pay" step and preserve
    // the current URL as `?next=` so /auth returns the user to the exact
    // same selection. Draft (priceId + email + name) is already in
    // sessionStorage via BookSession.writeDraft on every keystroke.
    if (status === 401 || status === 403) {
      const err = new Error("Sign in required to start checkout") as Error & {
        status: number;
        nextPath: string;
      };
      err.status = status;
      err.nextPath = window.location.pathname + window.location.search;
      throw err;
    }

    if (error || !data?.clientSecret) {
      const message =
        (error as InvokeError | null)?.message ||
        (data as { error?: string } | null)?.error ||
        "Failed to create checkout session";
      const err = new Error(message) as Error & { status: number | null };
      err.status = status;
      throw err;
    }

    return data.clientSecret as string;
  }, [priceId, quantity, customerEmail, customerName, userId, returnUrl]);

  useEffect(() => {
    const myAttempt = attempt + 1;
    activeAttemptRef.current = myAttempt;
    setState({ phase: "loading" });

    let cancelled = false;
    (async () => {
      try {
        const clientSecret = await requestClientSecret();
        if (cancelled || activeAttemptRef.current !== myAttempt) return;
        setState({ phase: "ready", clientSecret });
      } catch (e) {
        if (cancelled || activeAttemptRef.current !== myAttempt) return;
        const status = (e as { status?: number | null }).status ?? null;
        if (status === 401 || status === 403) {
          const nextPath = (e as { nextPath?: string }).nextPath
            ?? window.location.pathname + window.location.search;
          setState({ phase: "needs-auth", nextPath });
          return;
        }
        const classified = classifyError(status, (e as Error).message);
        setState({
          phase: "error",
          status,
          message: classified.message,
          retryable: classified.retryable,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [attempt, requestClientSecret]);

  const handleRetry = () => setAttempt((n) => n + 1);

  if (state.phase === "loading") {
    return (
      <div
        id="checkout"
        role="status"
        aria-live="polite"
        className="min-h-[600px] flex flex-col items-center justify-center gap-3 text-silver/80"
      >
        <Loader2 className="w-6 h-6 animate-spin text-cta" aria-hidden="true" />
        <p className="font-display uppercase tracking-[0.2em] text-xs">
          Preparing secure checkout…
        </p>
      </div>
    );
  }

  if (state.phase === "error") {
    const classified = classifyError(state.status, state.message);
    return (
      <div
        id="checkout"
        role="alert"
        aria-live="assertive"
        className="min-h-[600px] flex items-center justify-center p-6"
      >
        <div className="max-w-md w-full rounded-xl border border-border bg-navy-deep p-8 text-center shadow-lg">
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-cta/10">
            <AlertTriangle className="h-6 w-6 text-cta" aria-hidden="true" />
          </div>
          <h3 className="font-display uppercase tracking-[0.2em] text-lg text-white">
            {classified.title}
          </h3>
          <p className="mt-3 text-sm text-silver/80">{state.message}</p>
          {state.status !== null && (
            <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-silver/40">
              Reference: error {state.status}
            </p>
          )}
          {classified.retryable && (
            <button
              type="button"
              onClick={handleRetry}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-cta px-6 py-3 font-display uppercase tracking-[0.2em] text-xs text-white shadow-cta transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cta focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Try again
            </button>
          )}
          <p className="mt-4 text-xs text-silver/60">
            Your details are saved — retrying will not charge you twice.
          </p>
        </div>
      </div>
    );
  }

  if (state.phase === "needs-auth") {
    const href = `/auth?next=${encodeURIComponent(state.nextPath)}`;
    return (
      <div
        id="checkout"
        role="alert"
        aria-live="assertive"
        className="min-h-[600px] flex items-center justify-center p-6"
      >
        <div className="max-w-md w-full rounded-xl border border-border bg-navy-deep p-8 text-center shadow-lg">
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-cta/10">
            <LogIn className="h-6 w-6 text-cta" aria-hidden="true" />
          </div>
          <h3 className="font-display uppercase tracking-[0.2em] text-lg text-white">
            Sign in required to pay
          </h3>
          <p className="mt-3 text-sm text-silver/80">
            For your security, checkout requires a signed-in account. It only
            takes a moment — we'll bring you right back to this offer.
          </p>
          <Link
            to={href}
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-cta px-6 py-3 font-display uppercase tracking-[0.2em] text-xs text-white shadow-cta transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cta focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep"
          >
            <LogIn className="h-4 w-4" aria-hidden="true" />
            Sign in to continue
          </Link>
          <p className="mt-4 text-xs text-silver/60">
            Your selection and email are saved on this device.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div id="checkout" className="min-h-[600px]">
      <EmbeddedCheckoutProvider
        // A fresh key per successful fetch guarantees Stripe treats each
        // clientSecret as a new provider instance (its own contract forbids
        // swapping the secret on a mounted provider).
        key={state.clientSecret}
        stripe={getStripe()}
        options={{ fetchClientSecret: async () => state.clientSecret }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
}
